import { useCallback, useMemo } from "react";
import {
  ALL_GROUP_KEY,
  Badge,
  type BadgeMapEntry,
  Box,
  createColumnHelper,
  DataTableCellContent,
  DataTableCellTrigger,
  type DataTableColumnDef,
  DataTableEditableTextCell,
  type DataTableGroupType,
  Icon,
  Text,
} from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import {
  FIELD_MEDIA_TILE_SIZE,
  ResolvedMediaTile,
} from "#app/components/media/media-tiles";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { useMemberEntityResolver } from "#app/lib/data";
import { mediaKind } from "#app/lib/media-kind";
import { formatDateTime } from "#app/lib/utils/format-date";
import { useFormFillDialogActions } from "#app/lib/form-fill-dialog-context";
import { useLocalGroupPages } from "#app/lib/hooks/use-local-group-pages";
import type { IssueMediaItem } from "#app/hooks/issue-view/use-issue-media-items";
import type { IconName, Omittable, SortingState } from "@salestrak/ui";

// Flattened row: the accessor-backed columns (location, date) read plain
// properties; cells render from the underlying item. Shared by the Media tab
// and the attachments widget.
export interface MediaRow {
  photoId: string;
  index: number;
  source: IssueMediaItem["source"];
  origin: MediaSource;
  name: string;
  originalName: string;
  location: string;
  date: string | null;
  item: IssueMediaItem;
  userId: string | null | undefined;
}

export type MediaSource = "upload" | "field" | "form" | "note";

// DESIGN-TUNABLE: the field source reads as the quiet secondary chip (a
// palette hue like `purple` is available on the color axis if design wants
// it); Activity uses the soft info treatment.
export const MEDIA_SOURCES: Record<MediaSource, BadgeMapEntry & { group: string }> = {
  upload: { label: "Upload", group: "Uploads", color: "primary" },
  field: { label: "Custom field", group: "Custom fields", color: "secondary" },
  form: { label: "Form", group: "Forms", color: "secondary" },
  note: { label: "Activity", group: "Activity", color: "info" },
};

function mediaFrom({ item, location }: MediaRow): { icon: IconName; label: string; action: string } {
  if (item.source === "form") {
    const { form } = item.onFile;
    return { icon: "clipboard-check", label: form.name, action: `Open submission of ${form.name}` };
  }
  if (item.source === "field") {
    const label = item.field.name;
    return { icon: getFieldTypeMeta(item.field.type).icon, label, action: `Go to field ${label}` };
  }
  if (item.source === "upload") return { icon: "upload", label: location, action: "Open" };
  return { icon: "message", label: location, action: "Go to comment" };
}

export function mediaItemOrigin(item: IssueMediaItem): MediaSource {
  return item.source === "note_inline" ? "note" : item.source;
}

const h = createColumnHelper<MediaRow>();

function formQuestionLabel(onFile: Extract<IssueMediaItem, { source: "form" }>["onFile"]) {
  return onFile.item.label || (onFile.item.kind === "attachment" ? onFile.item.field.name : "Photos");
}

function nameSlot(item: IssueMediaItem): { key: string; base: string } | null {
  if (item.name || item.fileName) return null;
  if (item.source === "field") return { key: `field:${item.field.customFieldId}`, base: item.field.name };
  if (item.source === "form") {
    const { submission, item: question } = item.onFile;
    return { key: `form:${submission.submissionId}:${question.itemId}`, base: formQuestionLabel(item.onFile) };
  }
  if (item.source === "note" || item.source === "note_inline") {
    return { key: `note:${item.note.noteId}`, base: "Comment photo" };
  }
  return { key: "upload", base: "Uploaded file" };
}

function mediaNames(items: IssueMediaItem[]): string[] {
  const slots = items.map(nameSlot);
  const totals = new Map<string, number>();
  for (const slot of slots) if (slot) totals.set(slot.key, (totals.get(slot.key) ?? 0) + 1);
  const seen = new Map<string, number>();
  return items.map((item, i) => {
    const slot = slots[i];
    if (!slot) return item.name || item.fileName || "";
    const n = (seen.get(slot.key) ?? 0) + 1;
    seen.set(slot.key, n);
    return totals.get(slot.key) === 1 ? slot.base : `${slot.base} ${n}`;
  });
}

export function renamable(item: IssueMediaItem): boolean {
  return item.source !== "note_inline";
}

function rowUserId(item: IssueMediaItem): string | null | undefined {
  if (item.source === "upload") return item.upload.createdBy;
  if (item.source === "field") return item.uploadedBy;
  if (item.source === "form") return item.onFile.submission.submittedBy ?? null;
  return item.note.author?.userId ?? null;
}

export type MediaGroupBy = "all" | "source" | "type";

export const MEDIA_GROUP_BY_LABELS: Record<Exclude<MediaGroupBy, "all">, string> = {
  source: "Source",
  type: "Type",
};

const MEDIA_GROUPS: Record<Exclude<MediaGroupBy, "all">, { key: string; label: string }[]> = {
  source: (Object.keys(MEDIA_SOURCES) as MediaSource[]).map((key) => ({
    key,
    label: MEDIA_SOURCES[key].group,
  })),
  type: [
    { key: "image", label: "Photos" },
    { key: "video", label: "Videos" },
    { key: "pdf", label: "PDFs" },
    { key: "file", label: "Documents" },
  ],
};

function mediaGroupKey(row: MediaRow, groupBy: Exclude<MediaGroupBy, "all">): string {
  return groupBy === "type" ? mediaKind(row.item.contentType) : row.origin;
}

export function mediaRowBuckets(
  rows: MediaRow[],
  groupBy: MediaGroupBy,
): { key: string; label: string; data: MediaRow[] }[] {
  if (groupBy === "all") return rows.length ? [{ key: "all", label: "Attachments", data: rows }] : [];
  return MEDIA_GROUPS[groupBy].flatMap(({ key, label }) => {
    const data = rows.filter((row) => mediaGroupKey(row, groupBy) === key);
    return data.length ? [{ key, label, data }] : [];
  });
}

export function MediaAddedBy({
  userId,
  display = "visual",
}: {
  userId: string | null | undefined;
  display?: "visual" | "full";
}) {
  const memberEntity = useMemberEntityResolver();
  if (userId === undefined) return null;
  return (
    <EntityTag
      entity={(userId && memberEntity(userId)) || { kind: "user", id: userId ?? "system", user: null }}
      display={display}
      size="sm"
      system={!userId}
      interactive={false}
      preview={!!userId}
    />
  );
}

export function MediaSourceBadge({ source }: { source: MediaSource }) {
  return <Badge color={MEDIA_SOURCES[source].color}>{MEDIA_SOURCES[source].label}</Badge>;
}

interface UseIssueMediaRowsOptions {
  items: IssueMediaItem[];
  // Rows come out in this order so the list, grid and viewer all page alike.
  sorting?: SortingState;
  fromLink?: (row: MediaRow) => (() => void) | undefined;
}

const NO_SORT: SortingState = [];

export function useIssueMediaRows({ items, sorting = NO_SORT, fromLink }: UseIssueMediaRowsOptions) {
  const rows = useMemo<MediaRow[]>(() => {
    const names = mediaNames(items);
    const originals = mediaNames(items.map((item) => ({ ...item, name: null })));
    const built = items.map((item, index) => ({
        photoId: item.photoId,
        index,
        source: item.source,
        origin: mediaItemOrigin(item),
        name: names[index] ?? "",
        originalName: item.name && !item.fileName ? "" : (originals[index] ?? ""),
        location:
          item.source === "field"
            ? item.field.name
            : item.source === "form"
              ? formQuestionLabel(item.onFile)
              : item.source === "upload"
                ? "Upload"
                : "Comment",
        date:
          item.source === "field"
            ? null
            : item.source === "form"
              ? (item.onFile.submission.submittedAt ?? null)
              : item.source === "upload"
                ? item.upload.creationDate
                : item.note.creationDate,
        item,
        userId: rowUserId(item),
      }));
    const { id = "date", desc = true } = sorting[0] ?? {};
    const dir = desc ? -1 : 1;
    const sorted = built.sort((a, b) => {
      if (id === "name") return dir * a.name.localeCompare(b.name, undefined, { numeric: true });
      if (id === "location") return dir * mediaFrom(a).label.localeCompare(mediaFrom(b).label, undefined, { numeric: true });
      // A field photo carries no date; it sits after the dated files either way.
      if (a.date == null || b.date == null) return (a.date == null ? 1 : 0) - (b.date == null ? 1 : 0);
      return dir * (Date.parse(a.date) - Date.parse(b.date));
    });
    return sorted.map((row, index) => ({ ...row, index }));
  }, [items, sorting]);

  const lightboxMedia = useMemo(
    () =>
      rows.map((row) => ({
        id: row.photoId,
        contentType: row.item.contentType,
        fileName: row.item.fileName,
        name: row.name,
        originalName: row.originalName,
        details: [
          { label: "From", value: row.location, onClick: fromLink?.(row), action: mediaFrom(row).action },
          ...(row.userId !== undefined
            ? [{ label: "Added by", value: <MediaAddedBy userId={row.userId} display="full" /> }]
            : []),
          { label: "Source", value: <MediaSourceBadge source={row.origin} /> },
          ...(row.date ? [{ label: "Added", value: formatDateTime(row.date) }] : []),
        ],
      })),
    [fromLink, rows],
  );

  return { rows, lightboxMedia };
}

type UseIssueMediaColumnsOptions = Omittable<{
  onOpenLightbox: (index: number) => void;
  /** Quick links — jump to the field row on the summary tab… */
  onNavigateToField?: (customFieldId: string) => void;
  /** …or to the note on the activity tab. */
  onNavigateToNote?: (noteId: string, parentNoteId?: string | null) => void;
  /** Catch-all when a per-source link isn't wired (the attachments widget
   *  routes every jump to the Media tab, which has the full quick links). */
  onNavigateFallback?: () => void;
  tileSize?: number;
  renamingId?: string | null;
  onRename?: (row: MediaRow, name: string) => void;
  onRenameEnd?: () => void;
}>;

export function useMediaNavigate({
  onOpenLightbox,
  onNavigateToField,
  onNavigateToNote,
  onNavigateFallback,
}: Pick<
  UseIssueMediaColumnsOptions,
  "onOpenLightbox" | "onNavigateToField" | "onNavigateToNote" | "onNavigateFallback"
>): (row: MediaRow) => void {
  const { open: openFormFill } = useFormFillDialogActions();
  return useCallback(
    (row: MediaRow) => {
      if (row.item.source === "upload") {
        onOpenLightbox(row.index);
        return;
      }
      if (row.item.source === "form") {
        const { submission } = row.item.onFile;
        openFormFill({ issueId: submission.issueId, kind: "submission", submissionId: submission.submissionId });
        return;
      }
      if (row.item.source === "field" && onNavigateToField) {
        onNavigateToField(row.item.field.customFieldId);
        return;
      }
      if ((row.item.source === "note" || row.item.source === "note_inline") && onNavigateToNote) {
        const note = row.item.note;
        onNavigateToNote(note.noteId, note.parentNoteId);
        return;
      }
      onNavigateFallback?.();
    },
    [onNavigateFallback, onNavigateToField, onNavigateToNote, onOpenLightbox, openFormFill],
  );
}

export function useIssueMediaColumns({
  onOpenLightbox,
  onNavigateToField,
  onNavigateToNote,
  onNavigateFallback,
  tileSize = FIELD_MEDIA_TILE_SIZE,
  renamingId,
  onRename,
  onRenameEnd,
}: UseIssueMediaColumnsOptions): DataTableColumnDef<MediaRow>[] {
  const handleNavigate = useMediaNavigate({
    onOpenLightbox,
    onNavigateToField,
    onNavigateToNote,
    onNavigateFallback,
  });

  return useMemo<DataTableColumnDef<MediaRow>[]>(
    () => [
      {
        enableHiding: false,
        ...h.accessor({
          accessorKey: "name",
          header: "Name",
          enableSorting: true,
          meta: { minWidth: tileSize + 160, width: tileSize + 240 },
          cell: ({ row: { original: row } }) => {
            // Never shrinks: the rename's flex-1 input would crop the thumbnail.
            const tile = (
              <Box shrink={false}>
                <ResolvedMediaTile
                  photoId={row.photoId}
                  contentType={row.item.contentType}
                  size={tileSize}
                />
              </Box>
            );
            return renamingId === row.photoId && onRename ? (
              <DataTableEditableTextCell
                value={row.name}
                leading={tile}
                editing
                outline={false}
                weight="medium"
                aria-label={`Rename ${row.name}`}
                onCommit={(next) => onRename(row, next)}
                onEditingChange={(editing) => {
                  if (!editing) onRenameEnd?.();
                }}
              />
            ) : (
              <DataTableCellTrigger
                onClick={() => onOpenLightbox(row.index)}
                aria-label={`View ${row.name}`}
                outline={false}
              >
                <DataTableCellContent align="start" className="gap-2">
                  {tile}
                  <Text size="sm" weight="medium" truncate={1}>
                    {row.name}
                  </Text>
                </DataTableCellContent>
              </DataTableCellTrigger>
            );
          },
        }),
      },
      h.accessor({
        accessorKey: "location",
        header: "From",
        enableSorting: true,
        meta: { minWidth: 140, width: 280 },
        cell: ({ row: { original: row } }) => {
          const from = mediaFrom(row);
          return (
            <DataTableCellTrigger onClick={() => handleNavigate(row)} aria-label={from.action}>
              <DataTableCellContent>
                <Box direction="row" align="center" gap="xs" minWidth={0}>
                  <Icon name={from.icon} size="xs" color="muted" />
                  <Text size="sm" weight="medium" truncate={1}>
                    {from.label}
                  </Text>
                </Box>
              </DataTableCellContent>
            </DataTableCellTrigger>
          );
        },
      }),
      h.display({
        id: "addedBy",
        header: "Added by",
        meta: { width: 96, minWidth: 80 },
        cell: ({ row }) => (
          <DataTableCellContent align="start">
            <MediaAddedBy userId={row.original.userId} />
          </DataTableCellContent>
        ),
      }),
      h.display({
        id: "source",
        header: "Source",
        meta: { minWidth: 110, width: 190 },
        cell: ({ row }) => (
          <DataTableCellContent align="start">
            <MediaSourceBadge source={row.original.origin} />
          </DataTableCellContent>
        ),
      }),
      h.date({
        accessorKey: "date",
        header: "Added",
      }),
    ],
    [handleNavigate, onOpenLightbox, onRename, onRenameEnd, renamingId, tileSize],
  );
}

export function useIssueMediaGroups(
  rows: MediaRow[],
  /** The owning issue — flips reset the table's scroll window. */
  issueId: string,
  groupBy: MediaGroupBy = "all",
): DataTableGroupType<MediaRow>[] {
  const state = useLocalGroupPages(rows, `${issueId}:${groupBy}`);
  return useMemo(() => {
    if (rows.length === 0) return [{ data: [], key: ALL_GROUP_KEY, label: "", totalCount: 0 }];
    if (groupBy === "all") {
      const { total, ...groupProps } = state;
      return [{ key: "all", label: "Attachments", totalCount: total, ...groupProps }];
    }
    return mediaRowBuckets(rows, groupBy).map((bucket) => ({
      ...bucket,
      totalCount: bucket.data.length,
    }));
  }, [groupBy, rows, state]);
}
